import {
  blindTransferToSkill,
  blindTransferToAgent,
  blindTransferToPhone,
  initiateConference,
  TransferSkill,
  TransferAgent,
} from '../../services/cxone-sdk.service';
import { logCallActivity } from '../../services/call-log/call-log.service';
import { logger } from '../../utils/logger';

interface TransferActionContext {
  contactId: string;
  isConferenceMode: boolean;
  onComplete: (target?: string) => void;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  t: (key: string) => string;
}

export async function executeSkillTransfer(
  ctx: TransferActionContext,
  skill: TransferSkill
): Promise<void> {
  ctx.setError(null);
  ctx.setIsLoading(true);
  try {
    if (ctx.isConferenceMode) {
      await initiateConference(ctx.contactId, { skillId: skill.skillId });
      logger.lifecycle('[Transfer] Conference initiated', {
        targetType: 'skill',
        targetId: skill.skillId,
      });
      logCallActivity(
        'CALL_CONFERENCE_STARTED',
        {
          targetType: 'skill',
          targetId: skill.skillId,
          targetName: skill.skillName,
        },
        ctx.contactId
      );
      ctx.onComplete(skill.skillName);
    } else {
      await blindTransferToSkill(ctx.contactId, skill.skillId);
      logger.lifecycle('[Transfer] Skill transfer completed', { targetId: skill.skillId });
      logCallActivity(
        'CALL_TRANSFER',
        {
          targetType: 'skill',
          targetId: skill.skillId,
          targetName: skill.skillName,
        },
        ctx.contactId
      );
      ctx.onComplete();
    }
  } catch (err) {
    logger.error('[TransferPanel] Skill transfer failed:', err);
    ctx.setError(ctx.t('call:transfer.transferFailed'));
    ctx.setIsLoading(false);
  }
}

export async function executeAgentTransfer(
  ctx: TransferActionContext,
  agent: TransferAgent
): Promise<void> {
  ctx.setError(null);
  ctx.setIsLoading(true);
  try {
    if (ctx.isConferenceMode) {
      await initiateConference(ctx.contactId, { agentId: agent.agentId });
      logger.lifecycle('[Transfer] Conference initiated', {
        targetType: 'agent',
        targetId: agent.agentId,
      });
      logCallActivity(
        'CALL_CONFERENCE_STARTED',
        {
          targetType: 'agent',
          targetId: agent.agentId,
          targetName: agent.agentName,
        },
        ctx.contactId
      );
      ctx.onComplete(agent.agentName);
    } else {
      await blindTransferToAgent(ctx.contactId, agent.agentId);
      logger.lifecycle('[Transfer] Agent transfer completed', { targetId: agent.agentId });
      logCallActivity(
        'CALL_TRANSFER',
        {
          targetType: 'agent',
          targetId: agent.agentId,
          targetName: agent.agentName,
        },
        ctx.contactId
      );
      ctx.onComplete();
    }
  } catch (err) {
    logger.error('[TransferPanel] Agent transfer failed:', err);
    ctx.setError(ctx.t('call:transfer.transferFailed'));
    ctx.setIsLoading(false);
  }
}

export async function executePhoneTransfer(
  ctx: TransferActionContext,
  phoneNumber: string
): Promise<void> {
  if (!phoneNumber.trim()) {
    ctx.setError(ctx.t('call:transfer.enterPhoneNumberError'));
    return;
  }

  ctx.setError(null);
  ctx.setIsLoading(true);
  try {
    if (ctx.isConferenceMode) {
      await initiateConference(ctx.contactId, { phoneNumber });
      logger.lifecycle('[Transfer] Conference initiated', { targetType: 'phone' });
      logCallActivity(
        'CALL_CONFERENCE_STARTED',
        {
          targetType: 'phone',
          targetId: phoneNumber,
        },
        ctx.contactId
      );
      ctx.onComplete(phoneNumber);
    } else {
      await blindTransferToPhone(ctx.contactId, phoneNumber);
      logger.lifecycle('[Transfer] Phone transfer completed');
      logCallActivity(
        'CALL_TRANSFER',
        {
          targetType: 'phone',
          targetId: phoneNumber,
        },
        ctx.contactId
      );
      ctx.onComplete();
    }
  } catch (err) {
    logger.error('[TransferPanel] Phone transfer failed:', err);
    ctx.setError(ctx.t('call:transfer.transferFailed'));
    ctx.setIsLoading(false);
  }
}
